import NextImage from "next/image";
import { useCallback, useReducer, useRef, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { useAppContext } from "../../_context";
import { Clear } from "@material-ui/icons";
import { convertToPixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import {
  uploadPhoto,
  handleCropSubmit,
  centerAspectCrop,
  uploadClubPhoto,
} from "../../utilities/Helpers";
import BrowseToChooseFile from "./BrowseToChooseFile";
import UploadedFilesList from "./UploadedFilesList";
import DISPLAY_STATES from "./stateManagement/displayStates";
import {
  DISPLAY_STATE_ACTION,
  ADD_UPLOADS_ACTION,
  REMOVE_UPLOAD_ACTION,
  SET_SELECTED_ACTION,
  SET_CROP_ACTION,
  SET_COMPLETED_CROP_ACTION,
  SET_NEW_IMAGE_ACTION,
  RESET_NEW_IMAGE_ACTION,
} from "./stateManagement/uploadActions";
import uploadMultipleFileReducer, {
  initialState,
} from "./stateManagement/uploadMultipleFileReducer";
import CropModal from "./CropModal";

const processUpload = async ({
  upload,
  photoPrefix,
  onPhotoUpload,
  clubAlreadyCreated,
}) => {
  const result = clubAlreadyCreated
    ? await uploadClubPhoto({ file: upload, prefix: photoPrefix })
    : await uploadPhoto({
        file: upload,
        folder: "Courts",
        prefix: photoPrefix,
      });

  if (!result?.ok) {
    throw new Error("Failed to upload file");
  }

  const { url, largeImgUrl } = await result.json();

  if (!url) {
    throw new Error("Failed to upload file");
  }

  onPhotoUpload && onPhotoUpload(url, largeImgUrl);

  return {
    url,
    largeImgUrl,
  };
};

const UploadMultipleFileTarget = ({
  photoPrefix,
  maxFiles = 5,
  onPhotoUpload,
  clubAlreadyCreated = false,
}) => {
  const [{ tx }] = useAppContext();
  const [state, dispatch] = useReducer(uploadMultipleFileReducer, {
    ...initialState,
    maxFiles,
  });
  const imageToBeCroppedRef = useRef(null);
  const isDragAndDropActive =
    state.displayState === DISPLAY_STATES.INITIAL ||
    (state.displayState === DISPLAY_STATES.HAS_UPLOAD &&
      state.uploads.length < maxFiles);
  const showCropModal = !!(
    state.newImage && state.displayState !== DISPLAY_STATES.HAS_ERROR
  );

  // update completedCrop if the screen size changes
  useEffect(() => {
    const updateCompletedCrop = () => {
      if (imageToBeCroppedRef.current) {
        const { width, height } = imageToBeCroppedRef.current;

        dispatch({
          type: SET_COMPLETED_CROP_ACTION,
          completedCrop: convertToPixelCrop(state.crop, width, height),
        });
      }
    };

    window.addEventListener("resize", updateCompletedCrop);

    return () => window.removeEventListener("resize", updateCompletedCrop);
  }, [state.crop]);

  const onImageToBeCroppedLoad = (e) => {
    const { naturalWidth, naturalHeight, width, height } = e.currentTarget;
    const centeredCrop = centerAspectCrop(naturalWidth, naturalHeight, 16 / 9);

    dispatch({
      type: SET_CROP_ACTION,
      crop: centeredCrop,
      newImageWidth: naturalWidth,
      newImageHeight: naturalHeight,
    });
    dispatch({
      type: SET_COMPLETED_CROP_ACTION,
      completedCrop: convertToPixelCrop(centeredCrop, width, height),
    });
  };

  const uploadNewImage = async (toUpload) => {
    try {
      const newPhotoUpload = await processUpload({
        upload: toUpload,
        photoPrefix,
        onPhotoUpload,
        clubAlreadyCreated,
      });
      dispatch({ type: RESET_NEW_IMAGE_ACTION });
      dispatch({
        type: ADD_UPLOADS_ACTION,
        uploads: [newPhotoUpload],
        maxFilesError: tx["Only [n] file(s) uploaded"],
      });
    } catch (e) {
      dispatch({
        type: DISPLAY_STATE_ACTION,
        newState: DISPLAY_STATES.HAS_ERROR,
      });
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length) {
      dispatch({ type: SET_CROP_ACTION, crop: initialState.crop });
      const reader = new FileReader();
      reader.readAsDataURL(acceptedFiles[0]);
      reader.onload = () =>
        dispatch({ type: SET_NEW_IMAGE_ACTION, newImage: reader.result });
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: "image/png, image/jpeg, image/jpg, image/webp",
  });
  const dropProps = getRootProps();

  const onClick = dropProps.onClick;
  delete dropProps.onClick;

  const stateInitialHtml = (
    <div className="p-2 w-100">
      <NextImage
        src="/img/icons/add-photo.png"
        width={30}
        height={30}
        objectPosition="center"
        unoptimized={true}
      />
      <p className="mb-0 mt-2">{tx["Drag and drop photos to upload"]}</p>
      <BrowseToChooseFile onClick={() => {}} />
      <p className="mb-0">({tx["max [n] pics"].replace("[n]", maxFiles)})</p>
    </div>
  );

  const stateLoadingHtml = (
    <div className="p-2 text-primary text-center">
      <span className="spinner-border" />
    </div>
  );

  const stateErrorHtml = (
    <div
      className="text-center"
      onClick={() =>
        dispatch({
          type: DISPLAY_STATE_ACTION,
          newState: DISPLAY_STATES.INITIAL,
        })
      }>
      <span className="text-red">
        {tx["There was an error uploading your file."]}{" "}
      </span>
      <span className="text-primary fw-bold cursor-pointer">
        {tx["Please try again"]}
      </span>
      .
    </div>
  );

  let stateHasUploadsHtml = null;

  if (state.uploads.length >= maxFiles) {
    const imageSrc = state.uploads[state.selectedUpload].url;
    const imageOpts = {
      src: imageSrc,
      width: 275,
      height: 150,
      objectFit: "cover",
      unoptimized: true,
    };
    stateHasUploadsHtml = (
      <div className="position-relative font-size-0">
        <span
          className="delete-upload"
          onClick={() =>
            dispatch({ type: REMOVE_UPLOAD_ACTION, url: imageSrc })
          }>
          <Clear />
        </span>
        <NextImage {...imageOpts} />
      </div>
    );
  }

  const handleAddPhotoClick = () => {
    if (state.displayState === DISPLAY_STATES.UPLOADING) return;

    dispatch({
      type: DISPLAY_STATE_ACTION,
      newState: DISPLAY_STATES.UPLOADING,
    });
    handleCropSubmit({
      currentImage: imageToBeCroppedRef?.current,
      completedCrop: state.completedCrop,
      cb: uploadNewImage,
    });
  };

  const getTargetHtml = () => {
    if (state.displayState === DISPLAY_STATES.UPLOADING) {
      return stateLoadingHtml;
    }
    if (state.displayState === DISPLAY_STATES.HAS_ERROR) {
      return stateErrorHtml;
    }
    if (isDragAndDropActive) {
      return stateInitialHtml;
    }
    if (
      state.displayState === DISPLAY_STATES.HAS_UPLOAD &&
      state.uploads.length >= maxFiles
    ) {
      return stateHasUploadsHtml || "";
    }

    return "";
  };

  return (
    <div className="upload-multiple-file-target-container">
      <CropModal
        aspect={16 / 9}
        open={showCropModal}
        crop={state.crop}
        disabled={state.displayState === DISPLAY_STATES.UPLOADING}
        isLoading={state.displayState === DISPLAY_STATES.UPLOADING}
        imgRef={imageToBeCroppedRef}
        onCropChange={(c) => dispatch({ type: SET_CROP_ACTION, crop: c })}
        onCropComplete={(c) =>
          dispatch({ type: SET_COMPLETED_CROP_ACTION, completedCrop: c })
        }
        newImage={state.newImage}
        onNewImageLoad={onImageToBeCroppedLoad}
        onAddPhoto={handleAddPhotoClick}
        onCancel={() => dispatch({ type: RESET_NEW_IMAGE_ACTION })}
      />
      <input
        type="hidden"
        id="signup-avatar-urls"
        value={JSON.stringify(state.uploads.map((upload) => upload.url))}
      />
      <input
        type="hidden"
        id="signup-large-image-urls"
        value={JSON.stringify(
          state.uploads.map((upload) => upload.largeImgUrl),
        )}
      />
      <div
        className={`upload-multiple-file-target text-center d-flex align-items-center justify-content-center overflow-hidden ${
          state.uploads.length >= maxFiles ? "border-0" : ""
        }`}
        {...(isDragAndDropActive ? getRootProps() : {})}>
        <input {...getInputProps()} />
        {getTargetHtml()}
      </div>

      {!clubAlreadyCreated && (
        <UploadedFilesList
          uploads={state.uploads}
          setSelected={(index) =>
            dispatch({ type: SET_SELECTED_ACTION, index })
          }
          onAddFile={onClick}
          onRemoveFile={(url) => dispatch({ type: REMOVE_UPLOAD_ACTION, url })}
          selectedIndex={state.selectedUpload}
          maxFiles={maxFiles}
        />
      )}
    </div>
  );
};

export default UploadMultipleFileTarget;
